import React, { useState, useEffect, useRef } from 'react';
import { useWorkspace } from '../WorkspaceContext';
import { Plus, ChevronDown } from 'react-feather';
import { useDarkMode } from '../DarkModeContext';
import './WorkspaceSelector.css';

const WorkspaceSelector = () => {
    const { currentWorkspace, workspaces, selectWorkspace, createWorkspace, loading } = useWorkspace();
    const { darkMode } = useDarkMode();
    const [isOpen, setIsOpen] = useState(false);
    const [newWorkspaceName, setNewWorkspaceName] = useState('');
    const [showCreateInput, setShowCreateInput] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const inputRef = useRef(null);

    const handleSelect = (workspaceId) => {
        console.log('WorkspaceSelector: Available workspaces:', workspaces.map(w => ({ id: w._id, name: w.name })));
        console.log('WorkspaceSelector: Current workspace:', currentWorkspace ? { id: currentWorkspace._id, name: currentWorkspace.name } : 'none');
        console.log('WorkspaceSelector: Selecting workspace:', workspaceId);
        selectWorkspace(workspaceId);
        setIsOpen(false);
    };

    const handleCreateWorkspace = async () => {
        if (newWorkspaceName.trim() && !isCreating) {
            setIsCreating(true);
            try {
                const workspaceData = { name: newWorkspaceName.trim() };
                console.log('[WORKSPACE SELECTOR] Creating workspace with data:', workspaceData);
                await createWorkspace(workspaceData);
                setNewWorkspaceName('');
                setShowCreateInput(false);
                setIsOpen(false);
            } catch (error) {
                console.error('Error creating workspace:', error);
                // Keep the input open so user can try again
            } finally {
                setIsCreating(false);
            }
        }
    };

    const createInputContainerRef = useRef(null);
    const dropdownRef = useRef(null);

    // Auto-focus input when create input is shown and scroll into view
    useEffect(() => {
        if (showCreateInput && inputRef.current) {
            inputRef.current.focus();
            // Scroll the create input container into view
            if (createInputContainerRef.current) {
                createInputContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        }
    }, [showCreateInput]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.workspace-selector')) {
                setIsOpen(false);
                setShowCreateInput(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    if (loading) {
        return <div className="workspace-selector-loading">Loading...</div>;
    }

    return (
        <div className={`workspace-selector ${darkMode ? 'dark-mode' : ''}`}>
            <div className="current-workspace" onClick={() => setIsOpen(!isOpen)}>
                <span>{currentWorkspace ? currentWorkspace.name : 'Select Workspace'}</span>
                <ChevronDown size={16} className={isOpen ? 'rotate' : ''} />
            </div>
            {isOpen && (
                <div className="workspace-dropdown" ref={dropdownRef}>
                    {workspaces.map(ws => (
                        <div
                            key={ws._id}
                            className={`dropdown-item ${currentWorkspace && currentWorkspace._id === ws._id ? 'active' : ''}`}
                            onClick={() => handleSelect(ws._id)}
                        >
                            {ws.name}
                        </div>
                    ))}
                    <div className="dropdown-item create-new" onClick={() => setShowCreateInput(!showCreateInput)}>
                        <Plus size={16} /> Create New Workspace
                    </div>
                    {showCreateInput && (
                        <div className="create-input-container" ref={createInputContainerRef}>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Workspace Name"
                                value={newWorkspaceName}
                                onChange={(e) => setNewWorkspaceName(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleCreateWorkspace();
                                    }
                                }}
                                disabled={isCreating}
                            />
                            <button 
                                onClick={handleCreateWorkspace}
                                disabled={isCreating || !newWorkspaceName.trim()}
                            >
                                {isCreating ? 'Creating...' : 'Add'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default WorkspaceSelector;